import { useState, useCallback, useMemo, useRef, startTransition } from "react";

import {
  addOrUpdateKeyInOmnisearch,
  breakApartOmnisearch,
  removeKeyInOmnisearch,
} from "../../utils/omnisearchUtils";
import { parseFilterKeys, filterKeysEqual, FilterKeys } from "./omnisearchStore";

const DEBOUNCE_MS = 500;

export interface UseLocalOmnisearchOptions {
  /** Initial omnisearch value */
  initialValue?: string;
  /** Initial filters to apply (converted to omnisearch string) */
  initialFilters?: { field: string; value: string | boolean }[];
}

export interface UseLocalOmnisearchResult {
  /** Current omnisearch string (debounced) */
  omnisearch: string;
  /** Parsed filter keys for queries */
  filterKeys: FilterKeys;
  /** Get current omnisearch without triggering re-renders (like Zustand's getState) */
  getOmnisearch: () => string;
  /** Set omnisearch value (debounced for typing) */
  setOmnisearch: (value: string) => void;
  /** Set omnisearch value immediately (for programmatic updates) */
  setOmnisearchImmediate: (value: string) => void;
  /** Get a specific key value from omnisearch */
  getOmnisearchValue: (key: string) => string | undefined;
  /** Edit multiple key-value pairs in omnisearch */
  editOmnisearchKeyValues: (keyValues: [string, string][]) => void;
  /** Remove a key from omnisearch */
  removeOmnisearchKeyValue: (key: string) => void;
  /** Create a URL with the given key-values applied to omnisearch */
  createUrlFromOmnisearchKeyValues: (params: {
    keyValues: [string, string][];
    keysToKeep?: string[];
  }) => string;
}

/**
 * Local state omnisearch hook using useState.
 *
 * This hook manages omnisearch state locally without URL synchronization.
 * Useful for modals, drawers, or other non-URL-driven contexts.
 *
 * Key benefits:
 * - Simple useState-based state management
 * - No URL side effects
 * - Debounced updates for typing
 * - Stable action references
 *
 * @param options - Configuration options
 * @returns Omnisearch state and actions
 */
export function useLocalOmnisearch(
  options: UseLocalOmnisearchOptions = {}
): UseLocalOmnisearchResult {
  const { initialValue = "", initialFilters = [] } = options;

  // Compute initial omnisearch from initialFilters or initialValue
  const computedInitialValue = useMemo(() => {
    if (initialFilters.length > 0) {
      return initialFilters.map((f) => `${f.field}:${f.value}`).join(" ");
    }
    return initialValue;
  }, [initialValue, initialFilters]);

  // State for omnisearch value
  const [omnisearch, setOmnisearchState] = useState(computedInitialValue);

  // Use ref pattern to keep callbacks stable while accessing current omnisearch
  // This prevents callback recreation when omnisearch changes
  const omnisearchRef = useRef(omnisearch);
  omnisearchRef.current = omnisearch;

  // Track immediate value in ref for debouncing
  const immediateValueRef = useRef(computedInitialValue);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache filterKeys for referential stability
  const filterKeysRef = useRef<FilterKeys>(parseFilterKeys(computedInitialValue));

  // Compute filterKeys with referential stability
  const filterKeys = useMemo(() => {
    const newFilterKeys = parseFilterKeys(omnisearch);
    if (!filterKeysEqual(filterKeysRef.current, newFilterKeys)) {
      filterKeysRef.current = newFilterKeys;
    }
    return filterKeysRef.current;
  }, [omnisearch]);

  // Debounced setOmnisearch for typing
  const setOmnisearch = useCallback((value: string) => {
    immediateValueRef.current = value;

    // Clear any pending update
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
    }

    // Schedule debounced state update
    pendingUpdateRef.current = setTimeout(() => {
      startTransition(() => {
        setOmnisearchState(value);
      });
      pendingUpdateRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  // Immediate setOmnisearch for programmatic updates
  const setOmnisearchImmediate = useCallback((value: string) => {
    immediateValueRef.current = value;

    // Clear any pending debounce
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }

    startTransition(() => {
      setOmnisearchState(value);
    });
  }, []);

  // Helper functions - use ref pattern for stable callbacks
  // These callbacks are stable (never change) because they read from ref

  // Stable getter for current omnisearch (like Zustand's getState)
  const getOmnisearch = useCallback(() => omnisearchRef.current, []);

  const getOmnisearchValue = useCallback((key: string): string | undefined => {
    const { kvpValues } = breakApartOmnisearch(omnisearchRef.current);
    const kvp = kvpValues.find(([k]) => k === key);
    return kvp?.[1];
  }, []);

  const editOmnisearchKeyValues = useCallback(
    (keyValues: [string, string][]) => {
      let current = omnisearchRef.current;
      for (const [key, value] of keyValues) {
        current = addOrUpdateKeyInOmnisearch(current, key, value);
      }
      setOmnisearchImmediate(current);
    },
    [setOmnisearchImmediate]
  );

  const removeOmnisearchKeyValue = useCallback(
    (key: string) => {
      setOmnisearchImmediate(removeKeyInOmnisearch(omnisearchRef.current, key));
    },
    [setOmnisearchImmediate]
  );

  const createUrlFromOmnisearchKeyValues = useCallback(
    (params: { keyValues: [string, string][]; keysToKeep?: string[] }) => {
      const { keyValues, keysToKeep } = params;
      let result = "";

      if (keysToKeep) {
        const { kvpValues } = breakApartOmnisearch(omnisearchRef.current);
        for (const keyToKeep of keysToKeep) {
          const existingKvp = kvpValues.find(([k]) => k === keyToKeep);
          if (existingKvp) {
            result = addOrUpdateKeyInOmnisearch(result, existingKvp[0], existingKvp[1]);
          }
        }
      } else {
        result = omnisearchRef.current;
      }

      for (const [key, value] of keyValues) {
        result = addOrUpdateKeyInOmnisearch(result, key, value);
      }

      // Preserve every other query param that's already in the URL — only the
      // `omnisearch` slot is rewritten. The previous implementation rebuilt
      // the URL with just `omnisearch=…`, silently dropping siblings like
      // `?tab=overview` or `?ref=…`.
      const urlParams = new URLSearchParams(window.location.search);
      if (result.length) {
        urlParams.set("omnisearch", result);
      } else {
        urlParams.delete("omnisearch");
      }
      const queryString = urlParams.toString();
      return `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }${queryString ? `?${queryString}` : ""}`;
    },
    []
  );

  // Return stable result object
  return useMemo(
    () => ({
      omnisearch,
      filterKeys,
      getOmnisearch,
      setOmnisearch,
      setOmnisearchImmediate,
      getOmnisearchValue,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      createUrlFromOmnisearchKeyValues,
    }),
    [
      omnisearch,
      filterKeys,
      getOmnisearch,
      setOmnisearch,
      setOmnisearchImmediate,
      getOmnisearchValue,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      createUrlFromOmnisearchKeyValues,
    ]
  );
}
